import type { MouseEvent as ReactMouseEvent } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { BellOff, X } from "lucide-react";
import { NotificationItem } from "./NotificationItem";
import type { NotificationViewItem } from "./types";

const DRAWER_TRANSITION_MS = 300;
const TRANSITION_CLASS = "transition-all duration-300";
const DRAWER_TRANSITION_STYLE = {
  transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;
const OVERLAY_BASE_CLASS = "fixed z-40 bg-surface-accent/35";
const PANEL_BASE_CLASS =
  "fixed z-[45] border-border-subtle bg-surface-primary shadow-floating will-change-transform";
const DRAWER_STACK_CLASS = "fixed inset-0 z-40 isolate";
const NOTIFICATION_DRAWER_WIDTH_CLASS_NAME = "w-56 max-w-[calc(100vw-1rem)]";
const TOP_OFFSET_CLASS = "top-16 bottom-0 h-[calc(100dvh-4rem)] max-h-[calc(100dvh-4rem)]";
const OVERLAY_POSITION_CLASS = "left-0 right-0 bottom-0 top-16";

type AnimationState = "idle" | "entering" | "visible" | "exiting";

let bodyScrollLockCount = 0;
let previousBodyOverflow = "";
const openDrawerStack: string[] = [];

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  emptyTitle: string;
  emptySubtitle: string;
  retryLabel: string;
  dismissLabel: string;
  categoryLabels: {
    action: string;
    info: string;
  };
  items: NotificationViewItem[];
  isLoading?: boolean;
  errorMessage?: string | null;
  isUpdatingNotification?: (id: string) => boolean;
  getTimestampLabel: (createdAt: string) => string;
  resolveCopy: (
    descriptor: NotificationViewItem["view"]["title"] | undefined,
    fallback?: string,
  ) => string;
  onNavigate: (item: NotificationViewItem) => Promise<void> | void;
  onDelete: (item: NotificationViewItem) => Promise<void> | void;
  onRetry?: () => Promise<unknown> | void;
}

export const NotificationDrawer = ({
  isOpen,
  onClose,
  title,
  emptyTitle,
  emptySubtitle,
  retryLabel,
  dismissLabel,
  categoryLabels,
  items,
  isLoading = false,
  errorMessage = null,
  isUpdatingNotification,
  getTimestampLabel,
  resolveCopy,
  onNavigate,
  onDelete,
  onRetry,
}: NotificationDrawerProps) => {
  const drawerId = useId();
  const titleId = useId();
  const [animationState, setAnimationState] = useState<AnimationState>("idle");
  const animationStateRef = useRef<AnimationState>("idle");
  const timeoutRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);
  const entryFrameRef = useRef<number | null>(null);
  const [isAnimated, setIsAnimated] = useState(false);
  const panelRef = useRef<HTMLElement | null>(null);

  const isTopMostDrawer = useCallback(() => {
    return openDrawerStack[openDrawerStack.length - 1] === drawerId;
  }, [drawerId]);

  const setPhase = useCallback((state: AnimationState) => {
    animationStateRef.current = state;
    setAnimationState(state);
  }, []);

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const cancelFrame = useCallback(() => {
    if (frameRef.current && typeof window !== "undefined") {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      setPhase(isOpen ? "visible" : "idle");
      return;
    }

    if (isOpen) {
      clearTimeoutRef();
      if (
        animationStateRef.current === "visible" ||
        animationStateRef.current === "entering"
      ) {
        return;
      }

      setPhase("entering");
      cancelFrame();
      frameRef.current = window.requestAnimationFrame(() => {
        setPhase("visible");
        frameRef.current = null;
      });
      return;
    }

    if (
      animationStateRef.current === "idle" ||
      animationStateRef.current === "exiting"
    ) {
      return;
    }

    cancelFrame();
    setPhase("exiting");
    clearTimeoutRef();
    timeoutRef.current = window.setTimeout(() => {
      setPhase("idle");
      timeoutRef.current = null;
    }, DRAWER_TRANSITION_MS);
  }, [cancelFrame, clearTimeoutRef, isOpen, setPhase]);

  useEffect(() => {
    return () => {
      clearTimeoutRef();
      cancelFrame();
      if (entryFrameRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(entryFrameRef.current);
        entryFrameRef.current = null;
      }
    };
  }, [cancelFrame, clearTimeoutRef]);

  useEffect(() => {
    const isRendered = animationState !== "idle";

    if (!isRendered) {
      setIsAnimated(false);
      return;
    }

    if (typeof window === "undefined") {
      setIsAnimated(isOpen);
      return;
    }

    if (!isOpen) {
      setIsAnimated(false);
      return;
    }

    setIsAnimated(false);
    entryFrameRef.current = window.requestAnimationFrame(() => {
      setIsAnimated(true);
    });

    return () => {
      if (entryFrameRef.current) {
        window.cancelAnimationFrame(entryFrameRef.current);
        entryFrameRef.current = null;
      }
    };
  }, [animationState, isOpen]);

  const handleOverlayClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (isTopMostDrawer()) {
        onClose();
      }
    },
    [isTopMostDrawer, onClose],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    openDrawerStack.push(drawerId);
    return () => {
      const index = openDrawerStack.lastIndexOf(drawerId);
      if (index >= 0) {
        openDrawerStack.splice(index, 1);
      }
    };
  }, [drawerId, isOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return;
    }

    const { body } = document;
    if (bodyScrollLockCount === 0) {
      previousBodyOverflow = body.style.overflow;
      body.style.overflow = "hidden";
    }
    bodyScrollLockCount += 1;

    return () => {
      bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
      if (bodyScrollLockCount === 0) {
        body.style.overflow = previousBodyOverflow;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isTopMostDrawer()) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isTopMostDrawer, onClose]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return;
    }

    const container = panelRef.current;
    if (!container) {
      return;
    }

    const previousActive = document.activeElement as HTMLElement | null;
    const selector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const focusables = Array.from(
      container.querySelectorAll<HTMLElement>(selector),
    ).filter((node) => !node.hasAttribute("disabled"));

    (focusables[0] ?? container).focus();

    const handleTabTrap = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !isTopMostDrawer()) {
        return;
      }

      const nodes = Array.from(
        container.querySelectorAll<HTMLElement>(selector),
      ).filter((node) => !node.hasAttribute("disabled"));

      if (nodes.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleTabTrap);

    return () => {
      document.removeEventListener("keydown", handleTabTrap);
      if (previousActive && typeof previousActive.focus === "function") {
        previousActive.focus();
      }
    };
  }, [animationState, isOpen, isTopMostDrawer]);

  const shouldRender = animationState !== "idle";
  const isVisible =
    animationState === "visible" || animationState === "entering";

  if (!shouldRender || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className={DRAWER_STACK_CLASS}>
      <div
        className={clsx(
          OVERLAY_BASE_CLASS,
          TRANSITION_CLASS,
          OVERLAY_POSITION_CLASS,
          isVisible && isTopMostDrawer()
            ? "pointer-events-auto"
            : "pointer-events-none",
          isAnimated && isTopMostDrawer() ? "opacity-100" : "opacity-0",
        )}
        style={DRAWER_TRANSITION_STYLE}
        onClick={handleOverlayClick}
        aria-hidden
      />

      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-labelledby={titleId}
        tabIndex={-1}
        className={clsx(
          PANEL_BASE_CLASS,
          TRANSITION_CLASS,
          TOP_OFFSET_CLASS,
          NOTIFICATION_DRAWER_WIDTH_CLASS_NAME,
          "right-0 border-l",
          isAnimated
            ? "translate-x-0 opacity-100"
            : "translate-x-full opacity-0",
        )}
        style={DRAWER_TRANSITION_STYLE}
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-4 py-4">
          <h2 id={titleId} className="text-base font-semibold text-content-primary">
            {title}
          </h2>
          <button
            type="button"
            className="rounded-lg p-2 text-content-muted transition-colors hover:bg-surface-secondary hover:text-content-primary"
            aria-label={`Cerrar ${title}`}
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        {errorMessage ? (
          <div className="border-b border-state-danger/20 bg-state-dangerSoft px-5 py-3 text-sm text-state-danger">
            <p>{errorMessage}</p>
            {onRetry ? (
              <button
                type="button"
                className="mt-2 font-medium text-state-danger underline underline-offset-2"
                onClick={() => void onRetry()}
              >
                {retryLabel}
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  className="space-y-2 rounded-lg border border-border-subtle p-4"
                >
                  <div className="h-3 w-3/4 animate-pulse rounded bg-surface-secondary" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-surface-secondary" />
                </div>
              ))}
            </div>
          ) : null}

          {!isLoading && !errorMessage && items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-5 px-6 py-16 text-center text-content-secondary">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-secondary text-content-muted">
                <BellOff className="h-6 w-6" aria-hidden />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-content-primary">
                  {emptyTitle}
                </p>
                <p className="text-xs text-content-muted">{emptySubtitle}</p>
              </div>
            </div>
          ) : null}

          {!isLoading && !errorMessage && items.length > 0 ? (
            <ul className="space-y-3">
              {items.map((notification) => (
                <li key={notification.id}>
                  <NotificationItem
                    item={notification}
                    timestampLabel={getTimestampLabel(notification.createdAt)}
                    isUpdating={Boolean(
                      isUpdatingNotification?.(notification.id),
                    )}
                    resolveCopy={resolveCopy}
                    onNavigate={onNavigate}
                    onDelete={onDelete}
                    dismissLabel={dismissLabel}
                    categoryLabels={categoryLabels}
                  />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>
    </div>,
    document.body,
  );
};

export default NotificationDrawer;
